import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { 
  Store, 
  MapPin, 
  Package, 
  CreditCard, 
  Fuel,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  Plus,
  Trash2,
  Building2,
  Loader2,
  Sparkles,
  AlertCircle,
  Coffee
} from 'lucide-react';
import { getFuelGradeColor } from '../utils/fuelGradeColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORE_TYPES = [
  { value: 'convenience_store', label: 'Convenience Store', icon: Store, color: 'bg-blue-500/20 text-blue-400' },
  { value: 'fuel_station', label: 'Fuel Station', icon: Fuel, color: 'bg-amber-500/20 text-amber-400' },
  { value: 'property_rental', label: 'Property Rental', icon: Building2, color: 'bg-purple-500/20 text-purple-400' }
];

const DEFAULT_DEPARTMENTS = {
  convenience_store: [],
  fuel_station: [
    { name: 'Fuel Sales', field_type: 'amount' },
  ],
  property_rental: [
    { name: 'Rent Income', field_type: 'amount' },
    { name: 'Service Charges', field_type: 'amount' },
  ]
};

const DEFAULT_PAYMENT_TYPES = [
  { name: 'Cash', is_active: true },
  { name: 'Card Payments', is_active: true },
  { name: 'Vouchers', is_active: true },
];

// Services available for all stores
const DEFAULT_SERVICES = [
  { name: 'Lottery', key: 'lottery', enabled: false, has_amount: true, category: 'sales' },
  { name: 'Scratch Cards', key: 'scratch_cards', enabled: false, has_amount: true, category: 'sales' },
  { name: 'PayPoint', key: 'paypoint', enabled: false, has_amount: true, category: 'services' },
  { name: 'Payzone', key: 'payzone', enabled: false, has_amount: true, category: 'services' },
  { name: 'Western Union', key: 'western_union', enabled: false, has_amount: true, category: 'services' },
  { name: 'MoneyGram', key: 'moneygram', enabled: false, has_amount: true, category: 'services' },
];

// Additional services for Fuel Stations
const FUEL_STATION_SERVICES = [
  { name: 'Air Machine', key: 'air_machine', enabled: false, has_amount: true, category: 'fuel_services' },
  { name: 'Jet Wash', key: 'jet_wash', enabled: false, has_amount: true, category: 'fuel_services' },
  { name: 'Car Wash', key: 'car_wash', enabled: false, has_amount: true, category: 'fuel_services' },
];

const DEFAULT_FUEL_GRADES = [
  { name: 'Unleaded', opening_stock: 0, average_price: 0, selling_price: 0 },
  { name: 'Super Unleaded', opening_stock: 0, average_price: 0, selling_price: 0 },
  { name: 'Diesel', opening_stock: 0, average_price: 0, selling_price: 0 },
  { name: 'Premium Diesel', opening_stock: 0, average_price: 0, selling_price: 0 },
];

export const StoreSetupWizard = ({ onComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  
  // Form data
  const [storeInfo, setStoreInfo] = useState({
    name: '',
    location: '',
    email: '',
    phone: '',
    store_type: ''
  });
  
  const [departments, setDepartments] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState(DEFAULT_PAYMENT_TYPES.map((p, i) => ({ ...p, id: `pay-${i}` })));
  const [services, setServices] = useState([...DEFAULT_SERVICES]);
  const [fuelGrades, setFuelGrades] = useState([]);
  const [enableFuelGrades, setEnableFuelGrades] = useState(true);
  
  // Fuel Cards state
  const [globalFuelCardTypes, setGlobalFuelCardTypes] = useState([]);
  const [activeFuelCards, setActiveFuelCards] = useState([]);
  
  // Total Sales / Other Departments toggles
  const [enableTotalSales, setEnableTotalSales] = useState(true); // Always required
  const [enableOtherDepartments, setEnableOtherDepartments] = useState(false);
  
  // AMEX and Voucher tracking toggles
  const [amexTrackingEnabled, setAmexTrackingEnabled] = useState(false);
  const [voucherTrackingEnabled, setVoucherTrackingEnabled] = useState(false);
  
  // Department Monitoring for Margins
  const [departmentMonitoringEnabled, setDepartmentMonitoringEnabled] = useState(false);
  const [monitoredDepartments, setMonitoredDepartments] = useState([]);
  
  // Opening Balances & Banking
  const [openingBalances, setOpeningBalances] = useState({
    cash_holding: 0,
    safe: 0
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [newBankAccount, setNewBankAccount] = useState({ name: '', balance: 0 });
  const [newCreditCard, setNewCreditCard] = useState({ name: '', balance: 0, credit_limit: 0 });
  
  // Coffee Tracking
  const [coffeeTracking, setCoffeeTracking] = useState({
    coffee_sold_enabled: false,
    small_enabled: false,
    large_enabled: false,
    small_opening_reading: 0,
    large_opening_reading: 0
  });
  
  // New item forms
  const [newDept, setNewDept] = useState({ name: '', field_type: 'amount' });
  const [newFuelGrade, setNewFuelGrade] = useState({ name: '', opening_stock: '', average_price: '', selling_price: '' });
  const [newPayment, setNewPayment] = useState({ name: '' });

  // Fetch global fuel card types on mount
  useEffect(() => {
    const fetchFuelCardTypes = async () => {
      try {
        const response = await axios.get(`${API}/settings/fuel-card-types`);
        setGlobalFuelCardTypes(response.data.filter(fc => fc.is_active) || []);
      } catch (error) {
        console.error('Failed to fetch fuel card types:', error);
      }
    };
    fetchFuelCardTypes();
  }, []);

  const isFuelStation = storeInfo.store_type === 'fuel_station';
  
  const steps = [
    { title: 'Store Type', icon: Store, description: 'Choose your store type' },
    { title: 'Basic Info', icon: MapPin, description: 'Store details' },
    { title: 'Departments', icon: Package, description: 'Sales categories' },
    { title: 'Margin', icon: Package, description: 'Purchase tracking' },
    { title: 'Payments', icon: CreditCard, description: 'Payment methods' },
    { title: 'Balances', icon: Building2, description: 'Opening balances' },
    ...(isFuelStation && enableFuelGrades ? [{ title: 'Fuel Grades', icon: Fuel, description: 'Fuel configuration' }] : []),
    ...(isFuelStation ? [{ title: 'Fuel Cards', icon: CreditCard, description: 'Card acceptance' }] : []),
    { title: 'Review', icon: CheckCircle, description: 'Confirm setup' }
  ];
  
  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleStoreTypeSelect = (type) => {
    setStoreInfo({ ...storeInfo, store_type: type });
    // Pre-populate departments based on store type
    const defaultDepts = DEFAULT_DEPARTMENTS[type] || [];
    setDepartments(defaultDepts.map((d, i) => ({ ...d, id: `dept-${i}` })));
    
    // Pre-populate fuel grades and services for fuel stations
    if (type === 'fuel_station') {
      setFuelGrades(DEFAULT_FUEL_GRADES.map((g, i) => ({ ...g, id: `fuel-${i}` })));
      // Add fuel station specific services
      setServices([...DEFAULT_SERVICES, ...FUEL_STATION_SERVICES]);
    } else {
      setFuelGrades([]);
      setServices([...DEFAULT_SERVICES]);
    }
  };

  const addDepartment = () => {
    if (!newDept.name.trim()) return;
    // Check for duplicates
    if (departments.some(d => d.name.toLowerCase() === newDept.name.toLowerCase())) {
      return; // Could add toast here
    }
    setDepartments([...departments, { ...newDept, id: `dept-${Date.now()}` }]);
    setNewDept({ name: '', field_type: 'amount' });
  };

  const removeDepartment = (id) => {
    const dept = departments.find(d => d.id === id);
    // Prevent deletion of required departments (only Fuel Sales for fuel stations)
    if (dept && dept.name === 'Fuel Sales') {
      return; // Can't delete required departments
    }
    setDepartments(departments.filter(d => d.id !== id));
  };

  const addPaymentType = () => {
    if (!newPayment.name.trim()) return;
    if (paymentTypes.some(p => p.name.toLowerCase() === newPayment.name.toLowerCase())) {
      return;
    }
    setPaymentTypes([...paymentTypes, { ...newPayment, id: `pay-${Date.now()}`, is_active: true }]);
    setNewPayment({ name: '' });
  };

  const removePaymentType = (id) => {
    setPaymentTypes(paymentTypes.filter(p => p.id !== id));
  };

  const togglePaymentType = (id) => {
    setPaymentTypes(paymentTypes.map(p => 
      p.id === id ? { ...p, is_active: !p.is_active } : p
    ));
  };

  const toggleService = (key) => {
    setServices(services.map(s => 
      s.key === key ? { ...s, enabled: !s.enabled } : s
    ));
  };

  const addFuelGrade = () => {
    if (!newFuelGrade.name.trim()) return;
    if (fuelGrades.some(g => g.name.toLowerCase() === newFuelGrade.name.toLowerCase())) {
      return;
    }
    const openingStock = parseFloat(newFuelGrade.opening_stock) || 0;
    setFuelGrades([...fuelGrades, { 
      ...newFuelGrade, 
      id: `fuel-${Date.now()}`, 
      average_price: parseFloat(newFuelGrade.average_price) || 0,
      selling_price: parseFloat(newFuelGrade.selling_price) || 0,
      opening_stock: openingStock,
      current_stock: openingStock
    }]);
    setNewFuelGrade({ name: '', opening_stock: '', average_price: '', selling_price: '' });
  };

  const removeFuelGrade = (id) => {
    setFuelGrades(fuelGrades.filter(g => g.id !== id));
  };

  // Toggle fuel card selection
  const toggleFuelCard = (cardType, checked) => {
    if (checked) {
      setActiveFuelCards([...activeFuelCards, {
        card_type_id: cardType.id,
        card_name: cardType.name,
        category: cardType.category
      }]);
    } else {
      setActiveFuelCards(activeFuelCards.filter(afc => afc.card_type_id !== cardType.id));
    }
  };

  const canProceed = () => {
    // Get the current step title to determine validation
    const currentStepTitle = steps[currentStep]?.title;
    
    switch (currentStepTitle) {
      case 'Store Type': return !!storeInfo.store_type;
      case 'Basic Info': return storeInfo.name && storeInfo.location && storeInfo.email && storeInfo.phone;
      case 'Departments': return departments.length > 0;
      case 'Margin': return true; // Margin monitoring is optional
      case 'Payments': return paymentTypes.filter(p => p.is_active).length > 0;
      case 'Balances': return true; // Opening balances are optional (can be 0)
      case 'Fuel Grades':
        // Check that all fuel grades have selling prices
        return fuelGrades.length > 0 && fuelGrades.every(g => parseFloat(g.selling_price) > 0);
      case 'Fuel Cards': return true; // Fuel cards configuration is optional
      case 'Review': return true;
      default: return true;
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    
    // Build enabled services with full details
    const enabledServices = services.filter(s => s.enabled).map(s => ({
      key: s.key,
      name: s.name,
      has_amount: s.has_amount,
      category: s.category
    }));
    
    const storeData = {
      ...storeInfo,
      departments,
      payment_types: paymentTypes.filter(p => p.is_active),
      services: services.filter(s => s.enabled).map(s => s.key),
      enabled_services: enabledServices, // Full service details
      fuel_grades: (isFuelStation && enableFuelGrades) ? fuelGrades : [],
      enable_fuel_grades: isFuelStation ? enableFuelGrades : false,
      enable_total_sales: enableTotalSales,
      enable_other_departments: enableOtherDepartments,
      paid_out_types: [],
      fuel_cards_enabled: isFuelStation ? true : false,
      active_fuel_cards: isFuelStation ? activeFuelCards : [],
      amex_tracking_enabled: amexTrackingEnabled,
      voucher_tracking_enabled: voucherTrackingEnabled,
      // Department Monitoring
      department_monitoring_enabled: departmentMonitoringEnabled,
      monitored_departments: monitoredDepartments,
      // Opening Balances & Banking
      opening_balances: openingBalances,
      bank_accounts: bankAccounts,
      credit_cards: creditCards,
      // Coffee Tracking
      coffee_tracking: coffeeTracking
    };
    
    try {
      await onComplete(storeData);
    } catch (error) {
      console.error('Error creating store:', error);
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <Sparkles className="h-12 w-12 mx-auto text-primary mb-4" />
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                What type of store are you setting up?
              </h2>
              <p className="text-muted-foreground mt-2">
                This will help us pre-configure your departments and settings
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {STORE_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = storeInfo.store_type === type.value;
                return (
                  <Card 
                    key={type.value}
                    className={`cursor-pointer transition-all ${
                      isSelected 
                        ? 'ring-2 ring-primary border-primary' 
                        : 'hover:border-primary/50'
                    }`}
                    onClick={() => handleStoreTypeSelect(type.value)}
                    data-testid={`store-type-${type.value}`}
                  >
                    <CardContent className="p-6 text-center">
                      <div className={`w-16 h-16 mx-auto rounded-xl ${type.color} flex items-center justify-center mb-4`}>
                        <Icon className="h-8 w-8" />
                      </div>
                      <h3 className="font-semibold text-lg">{type.label}</h3>
                      {isSelected && (
                        <Badge className="mt-2 bg-primary/20 text-primary">Selected</Badge>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Fuel Grades Toggle for Fuel Station */}
            {storeInfo.store_type === 'fuel_station' && (
              <div className="mt-6 p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium flex items-center gap-2">
                      <Fuel className="h-4 w-4 text-amber-500" />
                      Enable Fuel Grades Management
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Track fuel stock, deliveries, and prices for each fuel grade
                    </p>
                  </div>
                  <Switch
                    checked={enableFuelGrades}
                    onCheckedChange={setEnableFuelGrades}
                    data-testid="enable-fuel-grades-toggle"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                Store Information
              </h2>
              <p className="text-muted-foreground mt-2">
                Enter your store's basic details
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Store Name *</Label>
                <Input
                  id="name"
                  value={storeInfo.name}
                  onChange={(e) => setStoreInfo({ ...storeInfo, name: e.target.value })}
                  placeholder="e.g., Main Street Store"
                  data-testid="wizard-store-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  value={storeInfo.location}
                  onChange={(e) => setStoreInfo({ ...storeInfo, location: e.target.value })}
                  placeholder="e.g., 123 Main St, London"
                  data-testid="wizard-store-location"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={storeInfo.email}
                  onChange={(e) => setStoreInfo({ ...storeInfo, email: e.target.value })}
                  placeholder="store@example.com"
                  data-testid="wizard-store-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  value={storeInfo.phone}
                  onChange={(e) => setStoreInfo({ ...storeInfo, phone: e.target.value })}
                  placeholder="020 1234 5678"
                  data-testid="wizard-store-phone"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                Departments
              </h2>
              <p className="text-muted-foreground mt-2">
                Configure your sales categories. We've added some defaults - you can modify or add more.
              </p>
            </div>

            {/* Amount Departments */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                Amount Departments (£)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {departments.filter(d => d.field_type === 'amount').map((dept) => (
                  <div key={dept.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <span className="font-medium">
                      {dept.name}
                      {dept.name === 'Fuel Sales' && (
                        <Badge variant="outline" className="ml-2 text-xs">Required</Badge>
                      )}
                    </span>
                    {dept.name !== 'Fuel Sales' && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeDepartment(dept.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Volume Departments */}
            {departments.filter(d => d.field_type === 'volume').length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                  Volume Departments (Qty)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {departments.filter(d => d.field_type === 'volume').map((dept) => (
                    <div key={dept.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="font-medium">{dept.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeDepartment(dept.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add New Department */}
            <div className="border border-border rounded-lg p-4 space-y-4">
              <h4 className="font-medium">Add Custom Department</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  placeholder="Department Name"
                  value={newDept.name}
                  onChange={(e) => setNewDept({ ...newDept, name: e.target.value })}
                  data-testid="wizard-dept-name"
                />
                <Select 
                  value={newDept.field_type} 
                  onValueChange={(value) => setNewDept({ ...newDept, field_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amount">Amount (£)</SelectItem>
                    <SelectItem value="volume">Volume (Qty)</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" onClick={addDepartment} data-testid="wizard-add-dept">
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Note: Lottery, Scratch Cards, and other services are configured in the next step under "Additional Services"
              </p>
            </div>

            {/* Total Sales (Z-Reading) - Always Required */}
            <div className="border border-primary/30 bg-primary/5 rounded-lg p-4 space-y-4">
              <h4 className="font-medium text-primary">Total Sales (Z-Reading)</h4>
              <p className="text-sm text-muted-foreground">
                This field is required for all stores. Enter the total from till Z-reading during daily sales entry.
              </p>
              <div className="p-3 rounded-lg border border-primary bg-primary/10">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  <span className="font-medium">Total Sales (Z-Reading)</span>
                  <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">Required</span>
                </div>
              </div>
            </div>

            {/* Other Departments Toggle */}
            <div className="border border-border rounded-lg p-4 space-y-3">
              <h4 className="font-medium">Optional Settings</h4>
              <div className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                enableOtherDepartments ? 'border-primary bg-primary/10' : 'border-border bg-muted/30'
              }`} onClick={() => setEnableOtherDepartments(!enableOtherDepartments)}>
                <div>
                  <span className={enableOtherDepartments ? 'font-medium' : 'text-muted-foreground'}>Other Departments</span>
                  <p className="text-xs text-muted-foreground">Auto-calculated difference (Total Sales - Named Departments)</p>
                </div>
                {enableOtherDepartments && <CheckCircle className="h-5 w-5 text-primary" />}
              </div>
            </div>
          </div>
        );

      case 3: // Margin Monitoring Step
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                Purchase Department Monitoring
              </h2>
              <p className="text-muted-foreground mt-2">
                Track purchase costs by department for margin analysis (Shop purchases only)
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Enable Department Monitoring</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Track costs per department when entering Shop purchase invoices
                  </p>
                </div>
                <Switch 
                  checked={departmentMonitoringEnabled}
                  onCheckedChange={setDepartmentMonitoringEnabled}
                  data-testid="wizard-dept-monitoring-toggle"
                />
              </div>

              {departmentMonitoringEnabled && (
                <div className="space-y-4 pt-3 border-t border-border">
                  <div className="space-y-2">
                    <Label>Select Departments to Monitor</Label>
                    <p className="text-xs text-muted-foreground">
                      Choose departments to track in Shop purchase invoices. Set VAT rate for each.
                    </p>
                    {departments.length === 0 ? (
                      <p className="text-xs text-amber-500 p-3 bg-amber-500/10 rounded-lg">
                        <AlertCircle className="h-4 w-4 inline mr-1" />
                        Add departments in the previous step first
                      </p>
                    ) : (
                      <div className="space-y-2 mt-3">
                        {departments.map((dept) => {
                          const monitoredDept = monitoredDepartments.find(m => m.department_id === dept.id);
                          const isMonitored = !!monitoredDept;
                          return (
                            <div 
                              key={dept.id}
                              className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                                isMonitored ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                              }`}
                            >
                              <div 
                                className="flex items-center gap-2 cursor-pointer flex-1"
                                onClick={() => {
                                  if (isMonitored) {
                                    setMonitoredDepartments(monitoredDepartments.filter(m => m.department_id !== dept.id));
                                  } else {
                                    setMonitoredDepartments([
                                      ...monitoredDepartments,
                                      { department_id: dept.id, department_name: dept.name, vat_rate: 20 }
                                    ]);
                                  }
                                }}
                              >
                                <div className={`h-4 w-4 rounded-sm border flex items-center justify-center ${
                                  isMonitored ? 'bg-primary border-primary' : 'border-muted-foreground'
                                }`}>
                                  {isMonitored && <CheckCircle className="h-3 w-3 text-primary-foreground" />}
                                </div>
                                <span className={isMonitored ? 'font-medium' : ''}>{dept.name}</span>
                              </div>
                              {isMonitored && (
                                <Select 
                                  value={String(monitoredDept?.vat_rate ?? 20)} 
                                  onValueChange={(value) => {
                                    setMonitoredDepartments(monitoredDepartments.map(m =>
                                      m.department_id === dept.id 
                                        ? { ...m, vat_rate: Number(value) }
                                        : m
                                    ));
                                  }}
                                >
                                  <SelectTrigger className="w-[120px]" onClick={(e) => e.stopPropagation()}>
                                    <SelectValue placeholder="VAT Rate" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="20">20% VAT</SelectItem>
                                    <SelectItem value="5">5% VAT</SelectItem>
                                    <SelectItem value="0">0% VAT</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {monitoredDepartments.length > 0 && (
                    <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <p className="text-sm text-blue-400">
                        <strong>Note:</strong> "Other" department will be automatically added. Its VAT is calculated as: Invoice VAT - Sum of Department VATs.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                Payment Methods & Services
              </h2>
              <p className="text-muted-foreground mt-2">
                Select which payment methods your store accepts
              </p>
            </div>

            {/* Payment Types */}
            <div className="space-y-4">
              <h3 className="font-semibold">Payment Methods</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {paymentTypes.map((payment) => (
                  <div 
                    key={payment.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                      payment.is_active 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border bg-muted/30'
                    }`}
                    onClick={() => togglePaymentType(payment.id)}
                  >
                    <span className={payment.is_active ? 'font-medium' : 'text-muted-foreground'}>
                      {payment.name}
                    </span>
                    {payment.is_active && <CheckCircle className="h-4 w-4 text-primary" />}
                  </div>
                ))}
              </div>
              
              {/* Add Custom Payment */}
              <div className="flex gap-2">
                <Input
                  placeholder="Add custom payment method"
                  value={newPayment.name}
                  onChange={(e) => setNewPayment({ name: e.target.value })}
                  className="max-w-xs"
                  data-testid="wizard-payment-name"
                />
                <Button type="button" variant="outline" onClick={addPaymentType}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {/* AMEX and Voucher Tracking Options */}
              <div className="border border-border rounded-lg p-4 space-y-3 mt-4">
                <h4 className="font-medium">Card Balance Tracking</h4>
                <p className="text-xs text-muted-foreground">
                  Enable separate tracking for AMEX and Voucher payments on the Card Balance Dashboard
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div 
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                      amexTrackingEnabled 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border bg-muted/30'
                    }`}
                    onClick={() => setAmexTrackingEnabled(!amexTrackingEnabled)}
                    data-testid="wizard-amex-tracking"
                  >
                    <div>
                      <span className={amexTrackingEnabled ? 'font-medium' : 'text-muted-foreground'}>Track AMEX Separately</span>
                      <p className="text-xs text-muted-foreground">Settle AMEX via Card Balance</p>
                    </div>
                    {amexTrackingEnabled && <CheckCircle className="h-4 w-4 text-primary" />}
                  </div>
                  <div 
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                      voucherTrackingEnabled 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border bg-muted/30'
                    }`}
                    onClick={() => setVoucherTrackingEnabled(!voucherTrackingEnabled)}
                    data-testid="wizard-voucher-tracking"
                  >
                    <div>
                      <span className={voucherTrackingEnabled ? 'font-medium' : 'text-muted-foreground'}>Track Voucher Separately</span>
                      <p className="text-xs text-muted-foreground">Settle Vouchers via Card Balance</p>
                    </div>
                    {voucherTrackingEnabled && <CheckCircle className="h-4 w-4 text-primary" />}
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Services */}
            <div className="space-y-4">
              <h3 className="font-semibold">Additional Services</h3>
              <p className="text-sm text-muted-foreground">
                Enable services your store offers. Sales amounts for enabled services can be entered during daily sales entry.
              </p>
              
              {/* Sales Related Services */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Sales Services</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {services.filter(s => s.category === 'sales').map((service) => (
                    <div 
                      key={service.key}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                        service.enabled 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border bg-muted/30'
                      }`}
                      onClick={() => toggleService(service.key)}
                      data-testid={`service-${service.key}`}
                    >
                      <span className={service.enabled ? 'font-medium' : 'text-muted-foreground'}>
                        {service.name}
                      </span>
                      {service.enabled && <CheckCircle className="h-4 w-4 text-primary" />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Money Transfer Services */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Money Transfer Services</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {services.filter(s => s.category === 'services').map((service) => (
                    <div 
                      key={service.key}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                        service.enabled 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border bg-muted/30'
                      }`}
                      onClick={() => toggleService(service.key)}
                      data-testid={`service-${service.key}`}
                    >
                      <span className={service.enabled ? 'font-medium' : 'text-muted-foreground'}>
                        {service.name}
                      </span>
                      {service.enabled && <CheckCircle className="h-4 w-4 text-primary" />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Fuel Station Services */}
              {isFuelStation && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Fuel Station Services</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {services.filter(s => s.category === 'fuel_services').map((service) => (
                      <div 
                        key={service.key}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                          service.enabled 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border bg-muted/30'
                        }`}
                        onClick={() => toggleService(service.key)}
                        data-testid={`service-${service.key}`}
                      >
                        <span className={service.enabled ? 'font-medium' : 'text-muted-foreground'}>
                          {service.name}
                        </span>
                        {service.enabled && <CheckCircle className="h-4 w-4 text-primary" />}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 5:
        // Balances step - for all store types
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                Opening Balances
              </h2>
              <p className="text-muted-foreground mt-2">
                Set initial balances for your store
              </p>
            </div>

            {/* Opening Balances */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cash Holding</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={openingBalances.cash_holding || 0}
                  onChange={(e) => setOpeningBalances({
                    ...openingBalances,
                    cash_holding: parseFloat(e.target.value) || 0
                  })}
                  data-testid="wizard-cash-holding"
                />
              </div>
              <div className="space-y-2">
                <Label>Safe Balance</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={openingBalances.safe || 0}
                  onChange={(e) => setOpeningBalances({
                    ...openingBalances,
                    safe: parseFloat(e.target.value) || 0
                  })}
                  data-testid="wizard-safe-balance"
                />
              </div>
            </div>

            {/* Bank Accounts */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Bank Accounts
              </h3>
              
              {bankAccounts.length > 0 && (
                <div className="space-y-2">
                  {bankAccounts.map((account, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                      <span className="flex-1 font-medium">{account.name}</span>
                      <span className="font-mono">£{(account.balance || 0).toFixed(2)}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setBankAccounts(bankAccounts.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  placeholder="Bank name"
                  value={newBankAccount.name}
                  onChange={(e) => setNewBankAccount({ ...newBankAccount, name: e.target.value })}
                  className="flex-1"
                />
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Balance"
                  value={newBankAccount.balance || ''}
                  onChange={(e) => setNewBankAccount({ ...newBankAccount, balance: parseFloat(e.target.value) || 0 })}
                  className="w-32"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (newBankAccount.name.trim()) {
                      setBankAccounts([...bankAccounts, { ...newBankAccount, id: `bank-${Date.now()}` }]);
                      setNewBankAccount({ name: '', balance: 0 });
                    }
                  }}
                  disabled={!newBankAccount.name.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Credit Cards */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Credit Cards
              </h3>
              
              {creditCards.length > 0 && (
                <div className="space-y-2">
                  {creditCards.map((card, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                      <span className="flex-1 font-medium">{card.name}</span>
                      <span className="font-mono text-red-400">-£{(card.balance || 0).toFixed(2)}</span>
                      <span className="text-xs text-muted-foreground">/ £{(card.credit_limit || 0).toFixed(2)}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setCreditCards(creditCards.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  placeholder="Card name"
                  value={newCreditCard.name}
                  onChange={(e) => setNewCreditCard({ ...newCreditCard, name: e.target.value })}
                  className="flex-1"
                />
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Balance"
                  value={newCreditCard.balance || ''}
                  onChange={(e) => setNewCreditCard({ ...newCreditCard, balance: parseFloat(e.target.value) || 0 })}
                  className="w-28"
                />
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Limit"
                  value={newCreditCard.credit_limit || ''}
                  onChange={(e) => setNewCreditCard({ ...newCreditCard, credit_limit: parseFloat(e.target.value) || 0 })}
                  className="w-28"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (newCreditCard.name.trim()) {
                      setCreditCards([...creditCards, { ...newCreditCard, id: `cc-${Date.now()}` }]);
                      setNewCreditCard({ name: '', balance: 0, credit_limit: 0 });
                    }
                  }}
                  disabled={!newCreditCard.name.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Coffee Tracking */}
            <div className="space-y-3 border-t pt-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Coffee className="h-4 w-4" />
                Coffee Tracking
              </h3>
              <p className="text-sm text-muted-foreground">
                Enable coffee machine tracking to monitor sales and variances
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <Label className="font-medium">POS Coffee Sold Tracking</Label>
                    <p className="text-xs text-muted-foreground">Track coffee sold quantity from POS</p>
                  </div>
                  <Switch
                    checked={coffeeTracking.coffee_sold_enabled}
                    onCheckedChange={(checked) => setCoffeeTracking({ ...coffeeTracking, coffee_sold_enabled: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <Label className="font-medium">Small Cup Machine Tracking</Label>
                    <p className="text-xs text-muted-foreground">Track small cup machine readings</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {coffeeTracking.small_enabled && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">Opening Reading:</Label>
                        <Input
                          type="number"
                          className="w-24 h-8"
                          value={coffeeTracking.small_opening_reading || 0}
                          onChange={(e) => setCoffeeTracking({ ...coffeeTracking, small_opening_reading: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                    )}
                    <Switch
                      checked={coffeeTracking.small_enabled}
                      onCheckedChange={(checked) => setCoffeeTracking({ ...coffeeTracking, small_enabled: checked })}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <Label className="font-medium">Large Cup Machine Tracking</Label>
                    <p className="text-xs text-muted-foreground">Track large cup machine readings</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {coffeeTracking.large_enabled && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">Opening Reading:</Label>
                        <Input
                          type="number"
                          className="w-24 h-8"
                          value={coffeeTracking.large_opening_reading || 0}
                          onChange={(e) => setCoffeeTracking({ ...coffeeTracking, large_opening_reading: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                    )}
                    <Switch
                      checked={coffeeTracking.large_enabled}
                      onCheckedChange={(checked) => setCoffeeTracking({ ...coffeeTracking, large_enabled: checked })}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 6:
        if (!isFuelStation) {
          // Skip to review for non-fuel stations
          return renderReviewStep();
        }
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                Fuel Grades
              </h2>
              <p className="text-muted-foreground mt-2">
                Configure the fuel grades you sell. All prices are in pence per litre.
              </p>
            </div>

            {/* Fuel Grades - Editable */}
            <div className="space-y-3">
              <h4 className="font-medium">Configure Fuel Grades</h4>
              <p className="text-sm text-muted-foreground">Enter selling price, cost price, and opening stock for each grade. Remove any grades you don't sell.</p>
              
              {fuelGrades.map((grade) => {
                const gradeColors = getFuelGradeColor(grade.name);
                return (
                <div key={grade.id} className="p-4 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${gradeColors.bg}`}>
                        <Fuel className={`h-5 w-5 ${gradeColors.text}`} />
                      </div>
                      <span className="font-medium text-lg">{grade.name}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFuelGrade(grade.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Selling Price (p/L) *</Label>
                      <div className="relative">
                        <Input
                          type="text"
                          inputMode="decimal"
                          placeholder="e.g., 149.9"
                          value={grade.selling_price || ''}
                          onChange={(e) => {
                            if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                              setFuelGrades(fuelGrades.map(g => 
                                g.id === grade.id ? { ...g, selling_price: e.target.value } : g
                              ));
                            }
                          }}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setFuelGrades(fuelGrades.map(g => 
                              g.id === grade.id ? { ...g, selling_price: val } : g
                            ));
                          }}
                          className="pr-10"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">p/L</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Average Cost (p/L)</Label>
                      <div className="relative">
                        <Input
                          type="text"
                          inputMode="decimal"
                          placeholder="e.g., 132.5"
                          value={grade.average_price || ''}
                          onChange={(e) => {
                            if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                              setFuelGrades(fuelGrades.map(g => 
                                g.id === grade.id ? { ...g, average_price: e.target.value } : g
                              ));
                            }
                          }}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setFuelGrades(fuelGrades.map(g => 
                              g.id === grade.id ? { ...g, average_price: val } : g
                            ));
                          }}
                          className="pr-10"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">p/L</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Opening Stock (Litres)</Label>
                      <div className="relative">
                        <Input
                          type="text"
                          inputMode="decimal"
                          placeholder="e.g., 5000"
                          value={grade.opening_stock || ''}
                          onChange={(e) => {
                            if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                              setFuelGrades(fuelGrades.map(g => 
                                g.id === grade.id ? { ...g, opening_stock: e.target.value, current_stock: e.target.value } : g
                              ));
                            }
                          }}
                          className="pr-6"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">L</span>
                      </div>
                    </div>
                  </div>
                  {grade.selling_price > 0 && (
                    <p className="text-xs text-green-500 mt-2">
                      = £{(grade.selling_price / 100).toFixed(3)} per litre
                    </p>
                  )}
                </div>
                );
              })}
            </div>

            {/* Add Custom Fuel Grade */}
            <div className="border border-dashed border-border rounded-lg p-4 space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Another Fuel Grade
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Grade Name</Label>
                  <Input
                    placeholder="e.g., AdBlue"
                    value={newFuelGrade.name}
                    onChange={(e) => setNewFuelGrade({ ...newFuelGrade, name: e.target.value })}
                    data-testid="wizard-fuel-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Selling Price (p/L)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g., 149.9"
                    value={newFuelGrade.selling_price || ''}
                    onChange={(e) => {
                      if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                        setNewFuelGrade({ ...newFuelGrade, selling_price: e.target.value });
                      }
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Cost (p/L)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g., 132.5"
                    value={newFuelGrade.average_price || ''}
                    onChange={(e) => {
                      if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                        setNewFuelGrade({ ...newFuelGrade, average_price: e.target.value });
                      }
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Opening Stock (L)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g., 5000"
                    value={newFuelGrade.opening_stock || ''}
                    onChange={(e) => {
                      if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                        setNewFuelGrade({ ...newFuelGrade, opening_stock: e.target.value });
                      }
                    }}
                  />
                </div>
              </div>
              <Button 
                type="button" 
                variant="outline" 
                onClick={addFuelGrade}
                disabled={!newFuelGrade.name}
                className="w-full sm:w-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Grade
              </Button>
            </div>

            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-400">
                <AlertCircle className="h-4 w-4 inline mr-2" />
                All prices are in pence (e.g., 149.9p = £1.499 per litre). Opening stock sets your initial book stock.
              </p>
            </div>
          </div>
        );

      case 7:
        // Fuel Cards step (only for fuel stations)
        if (!isFuelStation) {
          return renderReviewStep();
        }
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                Fuel Cards
              </h2>
              <p className="text-muted-foreground mt-2">
                Select which fuel card payments you accept at this station
              </p>
            </div>

            <div className="space-y-6">
              {/* Non Bunkering Cards */}
              <div className="space-y-3">
                <h4 className="font-semibold text-blue-500 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Non Bunkering Cards
                  <span className="text-xs text-muted-foreground font-normal">(Retail price - Weekly)</span>
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {globalFuelCardTypes.filter(fc => fc.category === 'non_bunkering').map((fc) => (
                    <label key={fc.id} className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-muted/50">
                      <Checkbox
                        checked={activeFuelCards.some(afc => afc.card_type_id === fc.id)}
                        onCheckedChange={(checked) => toggleFuelCard(fc, checked)}
                      />
                      <span className="text-sm font-medium">{fc.name}</span>
                    </label>
                  ))}
                </div>
                {globalFuelCardTypes.filter(fc => fc.category === 'non_bunkering').length === 0 && (
                  <p className="text-sm text-muted-foreground">No non-bunkering cards configured in Settings.</p>
                )}
              </div>

              {/* Bunkering Cards */}
              <div className="space-y-3">
                <h4 className="font-semibold text-orange-500 flex items-center gap-2">
                  <Fuel className="h-4 w-4" />
                  Bunkering Cards
                  <span className="text-xs text-muted-foreground font-normal">(Cost + Fee - Weekly)</span>
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {globalFuelCardTypes.filter(fc => fc.category === 'bunkering').map((fc) => (
                    <label key={fc.id} className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-muted/50">
                      <Checkbox
                        checked={activeFuelCards.some(afc => afc.card_type_id === fc.id)}
                        onCheckedChange={(checked) => toggleFuelCard(fc, checked)}
                      />
                      <span className="text-sm font-medium">{fc.name}</span>
                      <span className="text-xs text-muted-foreground">({fc.handling_fee_per_litre || 0}p/L)</span>
                    </label>
                  ))}
                </div>
                {globalFuelCardTypes.filter(fc => fc.category === 'bunkering').length === 0 && (
                  <p className="text-sm text-muted-foreground">No bunkering cards configured in Settings.</p>
                )}
              </div>

              {/* Key Fuels */}
              <div className="space-y-3">
                <h4 className="font-semibold text-green-500 flex items-center gap-2">
                  <Fuel className="h-4 w-4" />
                  Key Fuels
                  <span className="text-xs text-muted-foreground font-normal">(Diesel: Cost + Fee | Others: Retail - % - Monthly)</span>
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {globalFuelCardTypes.filter(fc => fc.category === 'key_fuels').map((fc) => (
                    <label key={fc.id} className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-muted/50">
                      <Checkbox
                        checked={activeFuelCards.some(afc => afc.card_type_id === fc.id)}
                        onCheckedChange={(checked) => toggleFuelCard(fc, checked)}
                      />
                      <span className="text-sm font-medium">{fc.name}</span>
                    </label>
                  ))}
                </div>
                  {globalFuelCardTypes.filter(fc => fc.category === 'key_fuels').length === 0 && (
                    <p className="text-sm text-muted-foreground">No Key Fuels cards configured in Settings.</p>
                  )}
                </div>

              {activeFuelCards.length > 0 && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-sm text-green-400">
                    <CheckCircle className="h-4 w-4 inline mr-2" />
                    {activeFuelCards.length} fuel card type{activeFuelCards.length !== 1 ? 's' : ''} selected
                  </p>
                </div>
              )}
            </div>

            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-400">
                <AlertCircle className="h-4 w-4 inline mr-2" />
                You can manage fuel card types in Settings → Fuel Card Types
              </p>
            </div>
          </div>
        );

      default:
        return renderReviewStep();
    }
  };

  const renderReviewStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
          Review & Create
        </h2>
        <p className="text-muted-foreground mt-2">
          Review your store configuration before creating
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Store Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Store className="h-5 w-5" />
              Store Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Name:</span>
              <span className="font-medium">{storeInfo.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type:</span>
              <Badge className={STORE_TYPES.find(t => t.value === storeInfo.store_type)?.color}>
                {STORE_TYPES.find(t => t.value === storeInfo.store_type)?.label}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Location:</span>
              <span>{storeInfo.location}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email:</span>
              <span>{storeInfo.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Phone:</span>
              <span>{storeInfo.phone}</span>
            </div>
          </CardContent>
        </Card>

        {/* Departments */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5" />
              Departments ({departments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {departments.map((dept) => (
                <Badge key={dept.id} variant="outline">
                  {dept.name}
                </Badge>
              ))}
            </div>
            {(enableTotalSales || enableOtherDepartments) && (
              <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                {enableTotalSales && (
                  <Badge variant="default" className="bg-primary">Total Sales (Z-Reading)</Badge>
                )}
                {enableOtherDepartments && (
                  <Badge variant="default" className="bg-blue-500">Other Departments (Auto)</Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payments */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Methods ({paymentTypes.filter(p => p.is_active).length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {paymentTypes.filter(p => p.is_active).map((pay) => (
                <Badge key={pay.id} variant="outline">
                  {pay.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <Card className="md:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Additional Services ({services.filter(s => s.enabled).length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {services.filter(s => s.enabled).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {services.filter(s => s.enabled).map((service) => (
                  <Badge 
                    key={service.key} 
                    variant="outline"
                    className={
                      service.category === 'sales' ? 'bg-yellow-500/10 border-yellow-500/30' :
                      service.category === 'fuel_services' ? 'bg-amber-500/10 border-amber-500/30' :
                      ''
                    }
                  >
                    {service.name}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">None selected</span>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Enabled services will appear on the Sales Entry screen for amount entry
            </p>
          </CardContent>
        </Card>

        {/* Opening Balances */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Opening Balances
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cash Holding:</span>
              <span className="font-mono">£{(openingBalances.cash_holding || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Safe:</span>
              <span className="font-mono">£{(openingBalances.safe || 0).toFixed(2)}</span>
            </div>
            {bankAccounts.length > 0 && (
              <div className="pt-2 border-t mt-2">
                <span className="text-muted-foreground">Bank Accounts:</span>
                <div className="ml-2 mt-1">
                  {bankAccounts.map((acc, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{acc.name}</span>
                      <span className="font-mono">£{(acc.balance || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {creditCards.length > 0 && (
              <div className="pt-2 border-t mt-2">
                <span className="text-muted-foreground">Credit Cards:</span>
                <div className="ml-2 mt-1">
                  {creditCards.map((card, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{card.name}</span>
                      <span className="font-mono text-red-400">-£{(card.balance || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Fuel Grades (if applicable) */}
        {isFuelStation && enableFuelGrades && fuelGrades.length > 0 && (
          <Card className="md:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Fuel className="h-5 w-5" />
                Fuel Grades ({fuelGrades.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                {fuelGrades.map((grade) => {
                  const gradeColors = getFuelGradeColor(grade.name);
                  return (
                  <div key={grade.id} className="p-2 bg-muted/50 rounded text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${gradeColors.bg}`}></span>
                      <span className="font-medium">{grade.name}</span>
                    </div>
                    <div className="text-muted-foreground ml-4">
                      {grade.selling_price > 0 ? (
                        <>
                          <span className="font-mono">{parseFloat(grade.selling_price).toFixed(1)}</span>p/L 
                          <span className="text-green-500 ml-1">(£{(parseFloat(grade.selling_price) / 100).toFixed(3)})</span>
                        </>
                      ) : (
                        <span className="text-yellow-500">Price not set</span>
                      )}
                    </div>
                    {grade.opening_stock > 0 && (
                      <div className="text-blue-400 text-xs ml-4">Stock: {parseFloat(grade.opening_stock).toLocaleString()}L</div>
                    )}
                  </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
        {isFuelStation && !enableFuelGrades && (
          <Card className="md:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Fuel className="h-5 w-5" />
                Fuel Grades
              </CardTitle>
            </CardHeader>
            <CardContent>
              <span className="text-sm text-muted-foreground">Fuel grades management is disabled</span>
            </CardContent>
          </Card>
        )}

        {/* Fuel Cards (if applicable) */}
        {isFuelStation && (
          <Card className="md:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Fuel Cards
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activeFuelCards.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {activeFuelCards.map((card, index) => (
                    <Badge 
                      key={index} 
                      variant="secondary"
                      className={
                        card.category === 'bunkering' ? 'bg-orange-500/20 text-orange-400' :
                        card.category === 'key_fuels' ? 'bg-green-500/20 text-green-400' :
                        'bg-blue-500/20 text-blue-400'
                      }
                    >
                      {card.card_name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-sm text-yellow-500">No fuel cards selected</span>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <div className="p-4 bg-muted/50 rounded-lg flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium">You can edit these settings anytime</p>
          <p className="text-muted-foreground">
            After creating your store, use the Edit button to modify any configuration.
          </p>
        </div>
      </div>
    </div>
  );

  // Handle the review step index
  // Steps: 0-Store Type, 1-Basic Info, 2-Departments, 3-Margin, 4-Payments, 5-Balances, 6-Fuel Grades (if fuel), 7-Fuel Cards (if fuel), last-Review
  const reviewStepIndex = isFuelStation ? (enableFuelGrades ? 8 : 7) : 6;
  const isReviewStep = currentStep === reviewStepIndex || 
    (currentStep === 6 && !isFuelStation) || 
    (currentStep === 7 && isFuelStation && !enableFuelGrades);

  return (
    <div className="space-y-6" data-testid="store-setup-wizard">
      {/* Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Step {currentStep + 1} of {steps.length}</span>
          <span className="font-medium">{steps[currentStep]?.title}</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Step Indicators */}
      <div className="hidden md:flex items-center justify-between">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;
          return (
            <div 
              key={index} 
              className={`flex items-center gap-2 ${
                isActive ? 'text-primary' : isCompleted ? 'text-green-500' : 'text-muted-foreground'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isActive ? 'bg-primary text-primary-foreground' : 
                isCompleted ? 'bg-green-500/20' : 'bg-muted'
              }`}>
                {isCompleted ? <CheckCircle className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
              </div>
              <span className="text-sm hidden lg:block">{step.title}</span>
              {index < steps.length - 1 && <ChevronRight className="h-4 w-4 mx-2 text-muted-foreground" />}
            </div>
          );
        })}
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="p-6">
          {renderStepContent()}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={currentStep === 0 ? onCancel : prevStep}
          data-testid="wizard-back-btn"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          {currentStep === 0 ? 'Cancel' : 'Back'}
        </Button>

        {isReviewStep ? (
          <Button 
            onClick={handleComplete} 
            disabled={saving}
            data-testid="wizard-create-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Create Store
              </>
            )}
          </Button>
        ) : (
          <Button 
            onClick={nextStep} 
            disabled={!canProceed()}
            data-testid="wizard-next-btn"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default StoreSetupWizard;
